/**
 * processCardImage.js — v228.11.0
 *
 * Shared by the camera-capture path (CardScanner) and the new upload / drag-drop
 * path (onboarding slots). Both produce the SAME cardData shape the pair hook +
 * apply logic expect: { data, url, name, extractedData }. OCR runs on the front,
 * and on insurance backs (for the card-printed payer ID) — ID backs are capture
 * only. Failures are non-fatal: we still return the image so the slot fills.
 */
import { aiAPI, filesAPI } from '../services/api'
import { isIdScanRefused } from './idScanAutofill'
import { uploadFailureOf } from './uploadRefusal'

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(r.result)
    r.onerror = () => reject(new Error('Could not read file'))
    r.readAsDataURL(file)
  })
}

// PROD-1190: opts.patientState is passed to the OCR request (an insurance card only).
export async function processCardImage(dataUrl, cardType, side, opts = {}) {
  const shouldOcr = side === 'front' || (String(cardType) !== 'ID' && side === 'back')
  let extractedData = null
  let idNameCheck = null
  if (shouldOcr) {
    try {
      const r = await aiAPI.extractCardData(dataUrl, cardType, side, opts)
      if (r && r.success && r.extracted) extractedData = r.extracted
      // TEST 1.02 R2 — the server's answer on whether this ID will be KEPT.
      if (r && r.idNameCheck && typeof r.idNameCheck === 'object') idNameCheck = r.idNameCheck
    } catch (e) {
      console.warn('[card] OCR failed (non-fatal):', e?.message || e)
    }
  }
  // v228.11.6: persist the image by REFERENCE (Supabase storage), never as base64
  // in the profile JSON — two base64 cards exceed Vercel's request limit (413).
  // Non-blocking: on failure we keep the local preview (`data`) for this session,
  // and the store's _slimCard drops the base64 so the profile PUT can't 413.
  // ★★ TEST 1.02 R2 — A REFUSED ID IS NOT UPLOADED. The server has already said it will not
  //   keep this card (the name on it is not the account's), so sending the image to storage
  //   would leave a copy of somebody else's document behind with nothing pointing at it.
  //   The server's profile writers refuse it regardless; this keeps the bytes off our
  //   storage in the ordinary case.
  if (isIdScanRefused({ idNameCheck })) {
    return { data: dataUrl, url: dataUrl, storagePath: null,
             name: `${String(cardType).toLowerCase()}_${side}_${Date.now()}.jpg`,
             extractedData, idNameCheck }
  }
  let storage = null
  try {
    storage = await filesAPI.uploadDataUrl(dataUrl, `profiles/cards/${Date.now()}_${String(cardType).toLowerCase()}_${side}`)
  } catch (e) { storage = { path: null, url: null, error: uploadFailureOf(e) } }
  return {
    data: dataUrl,                      // local preview + OCR source (not persisted)
    url: storage?.url || dataUrl,       // storage signed URL when upload succeeded
    storagePath: storage?.path || null, // durable reference saved to the profile
    name: `${String(cardType).toLowerCase()}_${side}_${Date.now()}.jpg`,
    extractedData,
    idNameCheck,
    // ★ P2 Release 3: { status, code } ONLY when the image was NOT stored; a stored card
    //   has no such key. The screen shows it (cardUploadFailureMessage), and a card with
    //   no storagePath never survives _slimCard, so the key never reaches the profile.
    ...(storage?.path ? {} : { uploadError: storage?.error || uploadFailureOf(null) }),
  }
}

/** Validate + read an uploaded File, returning processed cardData (or throws). */
export async function processCardFile(file, cardType, side, opts = {}) {
  if (!file || !String(file.type || '').startsWith('image/')) {
    throw new Error('Please choose an image file (JPG or PNG).')
  }
  const dataUrl = await readFileAsDataUrl(file)
  return processCardImage(dataUrl, cardType, side, opts)
}

/**
 * v228.11.7: persist a camera-captured card to storage. The CardScanner returns
 * base64 (it doesn't go through processCardImage), so the camera path needs its
 * own upload to get a durable storagePath — otherwise _slimCard drops the image
 * on save. Non-blocking and idempotent: returns the card unchanged if it already
 * has a storagePath or has no base64 to upload, and never throws.
 */
export async function persistCardData(cardData, cardType, side) {
  if (!cardData || cardData.storagePath) return cardData
  // TEST 1.02 R2 — the camera path's card is refused by the same test, and not uploaded.
  if (isIdScanRefused(cardData)) return cardData
  const src = cardData.data || cardData.url
  if (typeof src !== 'string' || !src.startsWith('data:')) return cardData
  let uploadError = uploadFailureOf(null)
  try {
    const storage = await filesAPI.uploadDataUrl(src, `profiles/cards/${Date.now()}_${String(cardType).toLowerCase()}_${side}`)
    if (storage && storage.path) {
      const { uploadError: _stored, ...rest } = cardData
      return { ...rest, url: storage.url || cardData.url, storagePath: storage.path }
    }
    if (storage && storage.error) uploadError = storage.error
  } catch (e) { uploadError = uploadFailureOf(e) }
  // ★ P2 Release 3: the image was NOT stored. _slimCard still drops the base64 so the save
  //   cannot 413, and the card now says why it will not survive the save.
  return { ...cardData, uploadError }
}
